<template>
  <div class="container text-center">
    <!-- Process Button -->
    <button
      v-b-modal="'processPaymentModalFirst'"
      class="btn btn-primary btn-block"
      :disabled="isProcessing"
    >
      <span v-if="isProcessing">
        <i class="spinner-border spinner-border-sm"></i> Processing...
      </span>
      <span v-else>
        Process - {{ convertCurrencyToLocal(orderAmount) }}
      </span>
    </button>

    <!-- Main Payment Modal -->
    <b-modal id="processPaymentModalFirst" hide-footer hide-title>
      <div class="text-center">
        <h1>Order Amount</h1>
        <hr />
        <h1 style="font-size: 4em; font-weight: 900;">
          {{ convertCurrencyToLocal(orderAmount) }}
        </h1>

        <!-- Toggle Split Payment / Hold Order -->
        <span class="d-inline-flex align-items-center" style="gap: 0.75rem;">
          <a href="#" @click.prevent="splitAmount = true">Split Amount</a> |
          <a href="#" @click.prevent="holdOrder" class="text-secondary" title="Hold Order" :style="{ opacity: isProcessing ? 0.5 : 1 }">
            Hold
          </a>
        </span>
        <div v-if="splitAmount" class="my-2">
          <input
            class="form-control text-center"
            type="number"
            v-model.number="splitAmountValue"
            placeholder="Enter amount to pay"
            required
          />
        </div>
        <hr />
      </div>

      <!-- M-Pesa Polling Loader -->
      <div v-if="mpesaLoaderAwait" class="text-center my-3">
        <div class="spinner-border text-secondary" role="status">
          <span class="sr-only">Loading...</span>
        </div>
        <p>Waiting for M-Pesa confirmation...</p>
        <a
          href="#"
          class="d-block text-muted"
          @click.prevent="processMpesaNoValidation(paymentMethod, splitAmount ? splitAmountValue : orderAmount)"
        >
          Skip Validation
        </a>
      </div>

      <!-- M-Pesa Matching Transactions -->
      <div v-if="customersPayment.length > 0">
        <table class="table table-hover">
          <tbody>
            <tr
              v-for="payment in customersPayment"
              :key="payment.id"
              @click="completeMpesaPayment(orderId, payment.id)"
              style="cursor: pointer"
            >
              <td><small>{{ payment.customer }}</small></td>
              <td><small>{{ payment.transaction_code }}</small></td>
              <td><small>{{ convertCurrencyToLocal(payment.amount) }}</small></td>
              <td><i class="bi bi-check-circle text-success"></i></td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Payment Options -->
      <div class="btn-group w-100 mb-3">
        <a
          v-for="paymentGateway in paymentGateways"
          :key="paymentGateway.id"
          @click="checkoutPayment(paymentGateway.id, paymentGateway.payment_method)"
          :disabled="isProcessing"
          :class="[
            'btn text-capitalize btn-sm',
            paymentGateway.payment_method === 'M-pesa' ? 'btn-success' : 'btn-outline-primary'
          ]"
        >
          <span v-if="isProcessing">
            <i class="spinner-border spinner-border-sm"></i>
          </span>
          <span v-else>{{ paymentGateway.payment_method }}{{ paymentGateway.account_label ? ' · ' + paymentGateway.account_label : paymentGateway.account_number ? ' · ' + paymentGateway.account_number : '' }}</span>
        </a>
      </div>

      <!-- Errors -->
      <div v-if="submittingError" class="alert alert-danger" role="alert">
        {{ submittingError }}
      </div>
      <div v-if="paymentProcessingError" class="alert alert-warning" role="alert">
        {{ paymentProcessingError }}
      </div>

      <!-- Payment Validation Waiting UI -->
      <div v-if="validationActive && orderId">
        <hr />
        <payment-validation-waiting
          :expected-amount="validationAmount"
          record-type="order"
          :record-id="orderId"
          :payment-method="validationPaymentMethod"
          :payment-method-id="selectedModeOfPayment"
          @validated="onValidated"
        />
      </div>
    </b-modal>

    <!-- M-Pesa STK from the company's own Paybill/Till (shared with checkout) -->
    <mpesa-stk-modal
      ref="mpesaStk"
      :order-id="orderId"
      :amount="Number(stkAmount || 0)"
      :default-phone="customerPhone"
      @paid="onStkPaid"
      @skip="onStkSkip"
    />

    <!-- Cash Payment Modal -->
    <b-modal ref="paidAmountmodal" hide-footer hide-title>
      <div class="container py-3">
        <!-- Cash Amount -->
        <div class="row align-items-center mb-3">
          <div class="col-md-6">
            <h5 class="text-primary mb-0">Cash Amount</h5>
            <small class="text-muted">Amount of cash received from the customer</small>
          </div>
          <div class="col-md-6">
            <input
              class="form-control"
              v-model.number="paidAmount"
              type="number"
              placeholder="Enter cash received"
            />
            <small class="text-muted">Leave blank to confirm without recording change</small>
            <div class="d-flex flex-wrap mt-1" style="gap:0.25rem;">
              <button v-for="d in [50, 100, 200, 500, 1000]" :key="d"
                  class="badge badge-secondary"
                  style="border:none;cursor:pointer;font-size:0.72rem;padding:3px 6px;"
                  @click="paidAmount = d">
                {{ d }}
              </button>
            </div>
          </div>
        </div>

        <!-- Order Amount -->
        <div class="row align-items-center mb-3">
          <div class="col-md-6">
            <h5 class="text-primary mb-0">Order Amount</h5>
            <small class="text-muted">Total amount of the order</small>
          </div>
          <div class="col-md-6">
            <h4 class="font-weight-bold mb-0">{{ convertCurrencyToLocal(orderAmount) }}</h4>
          </div>
        </div>

        <!-- Change -->
        <div class="row align-items-center mb-4">
          <div class="col-md-6">
            <h5 class="text-primary mb-0">Change</h5>
            <small class="text-muted">Amount to return to the customer</small>
          </div>
          <div class="col-md-6">
            <h4 v-if="changeToCustomer > 0" class="font-weight-bold text-success mb-0">
              {{ convertCurrencyToLocal(changeToCustomer) }}
            </h4>
            <span v-else class="text-muted">No change to return</span>
          </div>
        </div>

        <!-- Confirm -->
        <div class="text-right">
          <small v-if="cashAmountError" class="text-danger d-block mb-2">{{ cashAmountError }}</small>
          <button class="btn btn-primary btn-block" @click="processCashPayment()" :disabled="isProcessing">
            <span v-if="isProcessing">
              <i class="spinner-border spinner-border-sm"></i> Processing...
            </span>
            <span v-else>Confirm Payment</span>
          </button>
        </div>
      </div>
    </b-modal>

    <!-- BNPL Customer Selection Modal -->
    <b-modal ref="selected_bnpl_customer_modal" hide-footer>
      <div>
        <p>Type to start searching customer</p>
        <div class="input-group">
          <input
            v-model="customerData.searchTerm"
            type="text"
            class="form-control"
            placeholder="Search by name, email, or phone number"
          />
        </div>
        <ul class="my-3" v-if="customerData.allcustomers.length > 0">
          <li v-for="customer in customerData.allcustomers" :key="customer.id" @click="selectBNPLCustomer(customer)">
            {{ customer?.full_name }} - {{ customer?.phone }}
            <hr class="m-0 my-1" />
          </li>
        </ul>
      </div>
    </b-modal>

    <!-- BNPL Modal -->
    <b-modal ref="bnpl_modal" hide-footer @show="prefillBnplDueDate">
      <div class="text-center"><h5>Buy Now Pay Later Details</h5></div>
      <small v-if="bnplForm.customerError !== ''" class="text-danger">{{ bnplForm.customerError }}</small>
      <form @submit.prevent="processBNPLPayment()">
        <div class="form-group">
          <label>Customer Details</label>
          <input
            v-if="bnplForm.orderDetails.customer?.full_name"
            type="text"
            class="form-control"
            v-model="bnplForm.orderDetails.customer.full_name"
            placeholder="Customer Name/Phone/Email"
            readonly
          />
          <p v-if="bnplForm.orderDetails?.customer">
            <a href="#" @click="selectedBNPLCustomerModal">Select Customer</a>
          </p>
          <p v-if="bnplForm.orderDetails?.customer">
            <a href="#" @click="selectedBNPLCustomerModal">Select To Change Customer</a>
          </p>
        </div>
        <div class="form-group">
          <label>Total Amount</label>
          <input type="integer" class="form-control" v-model="bnplForm.orderDetails.balance" readonly />
        </div>
        <div class="form-group">
          <label>Deposit</label>
          <input type="integer" class="form-control" v-model="bnplForm.depositAmount" />
        </div>
        <div class="form-group" v-if="Number(bnplForm.depositAmount) > 0">
          <label>Deposit Payment Method</label>
          <select class="custom-select" v-model="bnplForm.depositPaymentMethod">
            <option :value="null">--Select Payment--</option>
            <template v-for="payment in paymentGateways">
              <option
                v-if="payment.enabled && payment.payment_method.toLowerCase() !== 'buy now pay later'"
                :key="payment.id"
                :value="payment"
              >{{ payment.payment_method + (payment.account_label ? ' · ' + payment.account_label : payment.account_number ? ' · ' + payment.account_number : '') }}</option>
            </template>
          </select>
          <small v-if="bnplForm.depositPaymentMethodError" class="text-danger">{{ bnplForm.depositPaymentMethodError }}</small>
        </div>
        <div class="form-group">
          <label>Due Date</label>
          <date-range-picker v-model="bnplDuePicker" :single-date-picker="true" :auto-apply="true" :ranges="false" :append-to-body="true" :min-date="bnplForm.minimumDate" class="w-100">
                            <template #input="picker">
                                <span v-if="picker.startDate">{{ formatBnplDue(picker.startDate) }}</span>
                                <span v-else class="text-secondary">Pick the due date</span>
                            </template>
                        </date-range-picker>
        </div>
        <div class="form-group">
          <button type="submit" class="btn btn-outline-primary btn-block" :disabled="isProcessing">
            <span v-if="isProcessing">
              <i class="spinner-border spinner-border-sm"></i> Processing...
            </span>
            <span v-else>Process BNPL</span>
          </button>
        </div>
      </form>
    </b-modal>
  </div>
</template>

<script>
import moment from 'moment';
import DateRangePicker from 'vue2-daterange-picker/src/components/DateRangePicker';
import axios from 'axios';
import { mapGetters } from 'vuex';
import PaymentValidationWaiting from './PaymentValidationWaiting.vue';
import MpesaStkModal from './MpesaStkModal.vue';

export default {
  name: "MiniCheckout",
  components: { PaymentValidationWaiting, MpesaStkModal, DateRangePicker },
  data() {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const minDate = new Date(today);
    minDate.setMonth(minDate.getMonth() - 2);
    minDate.setDate(15);

    return {
      paymentGateways: [],
      selectedModeOfPayment: '',
      orderId: '',
      submittingError: '',
      paymentProcessingError: '',
      mpesaLoaderAwait: false,
      paymentMethod: false,
      checkmpesaresponsepoll: null,
      customersPayment: [],
      paidAmount: 0,
      changeToCustomer: 0,
      splitAmount: false,
      customerData: {
        searchTerm: '',
        allcustomers: [],
        filteredCustomers: []
      },
      splitAmountValue: 0,
      createdOrderDetails: {},
      isProcessing: false,
      cashAmountError: '',
      validationActive: false,
      validationAmount: 0,
      validationPaymentMethod: '',
      stkAmount: 0,
      bnplForm: {
        dueDate: '',
        depositAmount: 0.0,
        interest: false,
        minimumDate: minDate,
        paymentId: '',
        depositPaymentMethod: null,
        depositPaymentMethodError: '',
        customerError: '',
        orderDetails: {
          customer: { full_name: '' }
        }
      }
    };
  },
  computed: {
    // BNPL due date via the app's standard date picker; bnplForm.dueDate stays 'YYYY-MM-DD'.
    bnplDuePicker: {
      get() { return { startDate: this.bnplForm.dueDate ? new Date(this.bnplForm.dueDate) : null, endDate: this.bnplForm.dueDate ? new Date(this.bnplForm.dueDate) : null } },
      set(val) { this.bnplForm.dueDate = val && val.startDate ? moment(val.startDate).format('YYYY-MM-DD') : '' },
    },
    ...mapGetters(['userDetails']),
    orderAmount() {
      return this.$store.state.totalOrderAmount;
    },
    needsCashSession() {
      const settings = this.userDetails?.company?.companysettings;
      return !!(settings?.enable_opening_cash && !this.$store.state.cashSession);
    },
    paymentValidationEnabled() {
      return !!(this.userDetails?.company?.companysettings?.enable_payment_validation);
    },
    // "We have our own Paybill/Till" (stored as use_dukakit_paybill): M-Pesa
    // sends an STK prompt, same as the full checkout.
    usesStk() {
      return !!(this.userDetails?.company?.companysettings?.use_dukakit_paybill);
    },
    customerPhone() {
      return this.$store.state.customerdata?.phone || '';
    },
  },
  mounted() {
    this.getPaymentGateways();
  },
  beforeDestroy() {
    this.stopMpesaPolling();
  },
  watch: {
    paidAmount() {
      this.changeToCustomer = this.paidAmount - this.orderAmount;
    }
  },
  methods: {
    formatBnplDue(d) { return moment(d).format('ddd D MMM YYYY') },
    // Default due date from BNPL settings ("default time to pay"); only when
    // the cashier hasn't picked one.
    async prefillBnplDueDate() {
      if (this.bnplForm.dueDate) return
      try {
        const { data } = await axios.get('/api/bnpl/terms')
        if (!this.bnplForm.dueDate && data.default_due_date) this.bnplForm.dueDate = data.default_due_date
      } catch (e) { /* keep the manual date picker */ }
    },

    async checkoutPayment(id, mode) {
      if (this.isProcessing) return;

      if (this.needsCashSession) {
        this.$root.$emit('cash-session-required', {
          retry: () => this.checkoutPayment(id, mode),
          cancel: () => {},
        });
        return;
      }

      this.isProcessing = true;
      try {
        this.selectedModeOfPayment = id;
        this.paymentMethod = mode;
        await this.submitData();
      } finally {
        this.isProcessing = false;
      }
    },

    selectedBNPLCustomerModal() {
      this.$refs.selected_bnpl_customer_modal.show();
    },

    async processBNPLPayment() {
      this.bnplForm.depositPaymentMethodError = '';
      if (Number(this.bnplForm.depositAmount) > 0 && !this.bnplForm.depositPaymentMethod) {
        this.bnplForm.depositPaymentMethodError = 'Select how the deposit was paid';
        return;
      }

      this.isProcessing = true;
      try {
        if (this.bnplForm.orderDetails.customer !== null) {
          const results = await axios.post('/api/pay-via-bnpl', {
            ...this.bnplForm,
            depositPaymentMethod: this.bnplForm.depositPaymentMethod?.id ?? null,
          });
          if (results.status == 200) {
            this.redirectAfterPayment(this.bnplForm.orderDetails);
          }
        } else {
          this.bnplForm.customerError = 'You need to select a customer';
        }
      } catch (err) {
        this.$refs.bnpl_modal.hide();
        this.$toasts.push({ type: 'error', message: "An error occurred creating your order!" });
      } finally {
        this.isProcessing = false;
      }
    },

    async submitData() {
      if (this.orderId) return await this.routePayment();

      const data = {
        cart: JSON.stringify(this.$store.state.cart),
        customerdata: JSON.stringify(this.$store.state.customerdata),
        totalprice: JSON.stringify(this.$store.state.totalOrderAmount),
        discount: 0,
        addTo: false
      };

      try {
        const result = await axios.post('/api/order', data);
        this.orderId = result.data.id;
        this.createdOrderDetails = result.data;
        this.bnplForm.orderDetails = result.data;

        console.log("routing payment-----")
        await this.routePayment();
      } catch (error) {
        if (this.isCashSessionError(error)) {
          this.$root.$emit('cash-session-required', {
            retry: () => this.checkoutPayment(this.selectedModeOfPayment, this.paymentMethod),
            cancel: () => {},
          });
          return;
        }
        console.log(error);
        this.submittingError = error;
      }
    },

    async routePayment() {
      const method = this.paymentMethod?.toLowerCase();
      const amount = this.splitAmount ? this.splitAmountValue : this.orderAmount;

        console.log(method,amount)

      if (this.splitAmount) {
        await this.processSplitPayment(method, amount);
           console.log(method)
      } else {
                console.log(amount)
        await this.processFullPayment(method, amount);
      }
    },

    async processFullPayment(method, amount) {

      this.isProcessing = true;

      if (method === 'm-pesa' && this.usesStk) {
        this.openStk(amount);
        this.isProcessing = false;
        return;
      }

      // Payment validation intercept — block non-cash payments until SMS confirmed
      if (
        this.paymentValidationEnabled &&
        method !== 'cash' &&
        method !== 'buy now pay later'
      ) {
        this.validationActive        = true;
        this.validationAmount        = amount;
        this.validationPaymentMethod = this.paymentMethod || '';
        this.isProcessing            = false;
        return;
      }

      try {
        switch (method) {
          case 'm-pesa':
            this.mpesaLoaderAwait = true;
            this.startMpesaPolling(amount);
            break;

          case 'cash':
            this.$refs['paidAmountmodal'].show();
            break;

          case 'buy now pay later':
            this.bnplForm.paymentId = this.selectedModeOfPayment;
            this.bnplForm.depositPaymentMethod = null;
            this.bnplForm.depositPaymentMethodError = '';
            this.bnplForm.orderDetails.customer = this.$store.state.customerdata;
            this.bnplForm.orderDetails.balance = amount;
            this.$refs.bnpl_modal.show();
            break;

          default:
            const result = await axios.post('/api/full-payment', {
              paymentMethod: this.selectedModeOfPayment,
              amount: this.orderAmount,
              orderId: this.orderId
            });
            if (result.status === 200) {
              this.redirectAfterPayment(result.data);
            }
            break;
        }
      } catch (error) {
        if (this.isCashSessionError(error)) {
          this.$root.$emit('cash-session-required', {
            retry: () => this.checkoutPayment(this.selectedModeOfPayment, this.paymentMethod),
            cancel: () => {},
          });
          return;
        }
        this.paymentProcessingError = error.response?.data?.message || 'Payment failed';
        this.stopMpesaPolling();
      } finally {
        this.isProcessing = false;
      }
    },

    // PERF: Centralized, deduped M-Pesa polling with guaranteed cleanup
    startMpesaPolling(amount) {
      this.stopMpesaPolling(); // prevent duplicate intervals

      this.checkmpesaresponsepoll = setInterval(async () => {
        try {
          // Which order and how much: without them the server matches nothing.
          const checkerresults = await axios.get('/api/validate-transaction', {
            params: { record_type: 'order', record_id: this.orderId, expected_amount: amount }
          });
          if (
            checkerresults.status === 200 &&
            checkerresults.data.transaction_type === 'sms' &&
            checkerresults.data.transaction.length > 0
          ) {
            this.customersPayment = checkerresults.data.transaction;
            this.mpesaLoaderAwait = false;
            setTimeout(() => this.stopMpesaPolling(), 3000);
          }
        } catch (error) {
          console.error('Polling error:', error);
        }
      }, 5000);
    },

    stopMpesaPolling() {
      if (this.checkmpesaresponsepoll) {
        clearInterval(this.checkmpesaresponsepoll);
        this.checkmpesaresponsepoll = null;
      }
    },

    async processCashPayment() {
      const received = parseFloat(this.paidAmount) || 0;
      if (received > 0 && received < this.orderAmount) {
        this.cashAmountError = `Cash received (${this.convertCurrencyToLocal(received)}) is less than amount due (${this.convertCurrencyToLocal(this.orderAmount)})`;
        return;
      }
      this.cashAmountError = '';

      this.isProcessing = true;
      try {
        const cashData = received > 0
          ? { cash_received: received, change_given: Math.max(0, received - this.orderAmount) }
          : {};
        const result = await axios.post('/api/full-payment', {
          paymentMethod: this.selectedModeOfPayment,
          amount: this.orderAmount,
          orderId: this.orderId,
          ...cashData
        });
        if (result.status === 200) {
            console.log("cash payment",'--------------')
          this.redirectAfterPayment(result.data);
        }
        console.log(result.status,"status------")
      } catch (error) {
        if (this.isCashSessionError(error)) {
          this.$root.$emit('cash-session-required', {
            retry: () => this.checkoutPayment(this.selectedModeOfPayment, this.paymentMethod),
            cancel: () => {},
          });
          return;
        }
        this.paymentProcessingError = error.response?.data?.message || 'Payment failed';
      } finally {
        this.isProcessing = false;
      }
    },

    async processSplitPayment(method, amount) {

      this.isProcessing = true;

      try {
        switch (method) {
          case 'm-pesa':
            if (this.usesStk) {
              this.openStk(amount);
              break;
            }
            this.mpesaLoaderAwait = true;
            this.startMpesaPolling(amount);
            break;

          default:
            if (
              this.paymentValidationEnabled &&
              method !== 'cash' &&
              method !== 'buy now pay later'
            ) {
              this.validationActive        = true;
              this.validationAmount        = amount;
              this.validationPaymentMethod = this.paymentMethod || '';
              this.isProcessing            = false;
              return;
            }

            const result = await axios.post('/api/split-payment', {
              paymentMethod: this.selectedModeOfPayment,
              amount,
              orderId: this.orderId
            });
            if (result.status === 200 && result.data.balance === 0) {
              const link = this.userDetails?.company?.companysettings?.skip_checkout_done
                ? `/print_invoice/${result.data.id}`
                : `/paid/view-order-details/${result.data.id}`;
              window.location.replace(link);
            } else {
              this.mpesaLoaderAwait = false;
              this.splitAmount = true;
              this.splitAmountValue = result.data.balance;
            }
            break;
        }
      } catch (error) {
        if (this.isCashSessionError(error)) {
          this.$root.$emit('cash-session-required', {
            retry: () => this.checkoutPayment(this.selectedModeOfPayment, this.paymentMethod),
            cancel: () => {},
          });
          return;
        }
        this.paymentProcessingError = error;
      } finally {
        this.isProcessing = false;
      }
    },

    async completeMpesaPayment(order, payment) {

      this.isProcessing = true;

      try {
        const results = await axios.post('/api/order-mpesa-payment-validate', {
          transaction_id: payment,
          orderId: order,
          paymentMethod: this.selectedModeOfPayment,
          amount: this.splitAmount ? this.splitAmountValue : this.orderAmount
        });

        const link = this.userDetails?.company?.companysettings?.skip_checkout_done
          ? `/print_invoice/${order}`
          : `/paid/view-order-details/${order}`;

        if (results.status === 200 && results.data.balance === 0) {
          this.$store.commit('resetStore');
          window.location.replace(link);
        } else {
          this.splitAmount = true;
          this.splitAmountValue = results.data.balance;
          this.customersPayment = [];
        }
      } catch (error) {
        if (this.isCashSessionError(error)) {
          this.$root.$emit('cash-session-required', {
            retry: () => this.completeMpesaPayment(order, payment),
            cancel: () => {},
          });
          return;
        }
        this.paymentProcessingError = error;
      } finally {
        this.isProcessing = false;
      }
    },

    openStk(amount) {
      this.stkAmount = Number(amount);
      this.paymentProcessingError = '';
      this.$nextTick(() => this.$refs.mpesaStk?.open());
    },

    // No prompt (e.g. own Paybill without API keys): the previous behaviour,
    // waiting for the customer's M-Pesa SMS to match.
    onStkSkip() {
      this.mpesaLoaderAwait = true;
      this.startMpesaPolling(this.stkAmount);
    },

    // The prompt was paid: tie the M-Pesa transaction to the order (so it
    // shows as validated), record the payment, then finish or take the balance.
    async onStkPaid({ transactionsValidatedId }) {
      const amount = this.stkAmount;
      if (transactionsValidatedId) {
        try {
          await axios.post('/api/payment-validation/validate-by-transaction', {
            transactions_validated_id: transactionsValidatedId,
            record_type: 'order',
            record_id: this.orderId,
          });
        } catch (e) {
          console.error('Could not link M-Pesa transaction to order:', e);
        }
      }
      try {
        const remaining = Number(this.createdOrderDetails?.balance ?? this.orderAmount);
        const { data } = await axios.post(amount < remaining ? '/api/split-payment' : '/api/full-payment', {
          paymentMethod: this.selectedModeOfPayment,
          amount,
          orderId: this.orderId,
          transactions_validated_id: transactionsValidatedId || null,
        });
        this.createdOrderDetails = data;
        this.$refs.mpesaStk?.done();
        if (Number(data.balance) > 0) {
          this.splitAmount = true;
          this.splitAmountValue = data.balance;
          return;
        }
        setTimeout(() => this.redirectAfterPayment(data), 1500);
      } catch (e) {
        this.$refs.mpesaStk?.failCompletion(e.response?.data?.message || 'M-Pesa payment was received but the order could not be completed. Record it manually.');
      }
    },

    async processMpesaNoValidation(method, amount) {
      if (this.splitAmount) {
        await this.processSplitPayment(method, amount);
      } else {
        await this.processFullPayment(method, amount);
      }
    },

    redirectAfterPayment(orderDetails) {
      const link = this.userDetails?.company?.companysettings?.skip_checkout_done
        ? `/print_invoice/${orderDetails.id}`
        : `/paid/view-order-details/${orderDetails.id}`;
      this.$store.commit('resetStore');
       console.log("redirecting",'--------------')
      window.location.replace(link);
    },

    isCashSessionError(error) {
      return error?.response?.status === 423 &&
        error?.response?.data?.error === 'CASH_SESSION_REQUIRED';
    },

    async holdOrder() {
      if (this.isProcessing) return;
      this.isProcessing = true;
      try {
        if (!this.orderId) {
          const data = {
            cart: JSON.stringify(this.$store.state.cart),
            customerdata: JSON.stringify(this.$store.state.customerdata),
            totalprice: JSON.stringify(this.$store.state.totalOrderAmount),
            discount: 0,
            addTo: false
          };
          const result = await axios.post('/api/order', data);
          this.orderId = result.data.id;
        }
        this.$store.commit('resetStore');
        window.location.replace('/orders');
      } catch (error) {
        if (this.isCashSessionError(error)) {
          this.$root.$emit('cash-session-required', {
            retry: () => this.holdOrder(),
            cancel: () => {},
          });
          return;
        }
        this.$toasts?.push({ type: 'error', message: 'Failed to hold order' });
      } finally {
        this.isProcessing = false;
      }
    },

    async getPaymentGateways() {
      try {
        const results = await axios.get('/api/payments');
        this.paymentGateways = results.data;
      } catch (error) {
        console.error("Error loading gateways", error);
      }
    },

    // validate-by-transaction already linked the TX to the order.
    // All we need now is to complete the order (mark paid, deduct inventory).
    async onValidated(validationResult) {
      this.validationActive = false;
      const amount = this.splitAmount ? this.splitAmountValue : this.orderAmount;
      const endpoint = this.splitAmount ? '/api/split-payment' : '/api/full-payment';
      try {
        const result = await axios.post(endpoint, {
          paymentMethod:             this.selectedModeOfPayment,
          amount,
          orderId:                   this.orderId,
          transactions_validated_id: validationResult?.transaction?.id || null,
        });
        if (result.status === 200) {
          if (this.splitAmount && result.data.balance > 0) {
            this.splitAmountValue = result.data.balance;
          } else {
            this.redirectAfterPayment(result.data);
          }
        }
      } catch (error) {
        if (this.isCashSessionError(error)) {
          this.$root.$emit('cash-session-required', {
            retry: () => this.onValidated(validationResult),
            cancel: () => {},
          });
          return;
        }
        this.paymentProcessingError = error.response?.data?.message || 'Payment failed';
      }
    },
  }
};
</script>
